import * as React from "react";

import { Input } from "@/components/ui/input";

interface NumberInputProps extends Omit<
  React.ComponentProps<"input">,
  "type" | "value" | "onChange"
> {
  value?: number;
  /** Second argument is the formatted text ("" when empty), to tell "empty" from "0". */
  onChange?: (value: number, raw: string) => void;
  /** Max digits after the decimal comma (id-ID: "1.234,5"). 0 (default) = integers only. */
  decimals?: number;
  /** Show a literal 0 instead of an empty field (e.g. a stock count of zero is a real answer). */
  showZero?: boolean;
}

/** Splits typed text into integer digits and (when allowed) a comma + fraction digits. */
function parse(raw: string, decimals: number) {
  const commaAt = decimals > 0 ? raw.indexOf(",") : -1;
  const int = (commaAt === -1 ? raw : raw.slice(0, commaAt)).replace(/\D/g, "");
  const hasComma = commaAt !== -1;
  const frac = hasComma
    ? raw
        .slice(commaAt + 1)
        .replace(/\D/g, "")
        .slice(0, decimals)
    : "";
  return { int, hasComma, frac };
}

function format(raw: string, decimals: number) {
  const { int, hasComma, frac } = parse(raw, decimals);
  const head = int ? Number(int).toLocaleString("id-ID") : hasComma ? "0" : "";
  return hasComma ? `${head},${frac}` : head;
}

function toNumber(raw: string, decimals: number) {
  const { int, frac } = parse(raw, decimals);
  return Number(`${int || "0"}${frac ? `.${frac}` : ""}`);
}

function fromNumber(
  value: number | undefined,
  decimals: number,
  showZero: boolean,
) {
  if (value === undefined || (!value && !showZero)) return "";
  return format(String(value).replace(".", ","), decimals);
}

function NumberInput({
  className,
  value,
  onChange,
  decimals = 0,
  showZero = false,
  ref,
  ...props
}: NumberInputProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [prevValue, setPrevValue] = React.useState(value);
  const [display, setDisplay] = React.useState(() =>
    fromNumber(value, decimals, showZero),
  );

  if (prevValue !== value) {
    // Only overwrite what the user sees when the outside value really differs
    // (keeps a trailing "," while typing "0,").
    if (toNumber(display, decimals) !== (value ?? 0)) {
      setDisplay(fromNumber(value, decimals, showZero));
    }
    setPrevValue(value);
  }

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const rawValue = event.target.value;
    const cursor = event.target.selectionStart ?? rawValue.length;
    const significant = decimals > 0 ? /[\d,]/ : /\d/;
    const sigBeforeCursor = [...rawValue.slice(0, cursor)].filter((c) =>
      significant.test(c),
    ).length;

    const formatted = format(rawValue, decimals);

    setDisplay(formatted);
    onChange?.(toNumber(rawValue, decimals), formatted);

    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;

      let seen = 0;
      let nextCursor = formatted.length;
      for (let i = 0; i < formatted.length; i++) {
        if (significant.test(formatted.charAt(i))) seen++;
        if (seen === sigBeforeCursor) {
          nextCursor = i + 1;
          break;
        }
      }
      if (sigBeforeCursor === 0) nextCursor = 0;

      el.setSelectionRange(nextCursor, nextCursor);
    });
  }

  return (
    <Input
      type="text"
      inputMode={decimals > 0 ? "decimal" : "numeric"}
      autoComplete="off"
      {...props}
      ref={(node) => {
        inputRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      }}
      value={display}
      onChange={handleChange}
      className={className}
    />
  );
}

export { NumberInput };
export type { NumberInputProps };
